import { useState, Fragment } from 'react';
import { Button, Container, Box, Image, ThemeUIStyleObject } from 'theme-ui';
import ImgLoader from '../ImgLoader';
import useUpdateEffect from '~/hooks/components/use-update-effect';
import ImageCarousel from '../ImageCarousel';
import { type Image as ImageType } from '~/types';
import { CombinedMedia } from '~/utils/combine-media';

type Props = {
  image: CombinedMedia['mediaArrayWithThumbnails'][number];
  zoomedImages: CombinedMedia['mediaArrayWithVideos'];
  index: number;
  fromPdp?: boolean;
  forwardSx?: ThemeUIStyleObject;
  badge?: Queries.BadgeFragment | null;
};

const ImageZoom = ({ image, zoomedImages, index, fromPdp, forwardSx, badge }: Props) => {
  const zoomedImage = zoomedImages?.[index];
  const [isZoomed, setIsZoomed] = useState(false);

  useUpdateEffect(() => {
    document.body.style.overflow = isZoomed ? 'hidden' : 'initial';
    document.ontouchmove = (e) => (isZoomed ? e.preventDefault() : true);
  }, [isZoomed]);

  const handleImageClick = () => {
    if (zoomedImage) {
      setIsZoomed(true);
    }
  };

  return (
    <Fragment>
      {isZoomed && (
        <Container
          variant="fullwidth"
          sx={{
            position: 'fixed',
            width: '100%',
            height: '100%',
            background: 'rgba(256,256,256,0.75)',
            justifyContent: 'center',
            alignItems: 'center',
            top: 0,
            left: 0,
            zIndex: 100,
            animation: 'fade-in 0.2s',
          }}
        >
          {/* @ts-ignore */}
          <ImageCarousel
            images={zoomedImages}
            initialIndex={index}
            setIsZoomed={setIsZoomed}
            fromPdp={fromPdp}
            badge={badge}
          />
        </Container>
      )}
      <Box sx={{ height: '0%', width: '100%', pb: '100%', position: 'relative' }}>
        <Button
          variant="bare"
          key={image?.id}
          onClick={handleImageClick}
          sx={{
            width: '100%',
            height: '100%',
            position: 'absolute',
            cursor: zoomedImage ? 'zoom-in' : 'default',
            ...forwardSx,
          }}
        >
          {isThumbnailMedia(image) && image?.isThumbnail && (
            <Image
              role="none"
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 1,
                width: '33%',
              }}
              src="/play-button_small.png"
            />
          )}
          <ImgLoader
            image={image as ImageType}
            alt={image?.title || ''}
            forwardSx={{
              height: '100%',
              width: '100%',
            }}
            badge={index === 0 ? badge : undefined}
          />
        </Button>
      </Box>
    </Fragment>
  );
};
export default ImageZoom;

const isThumbnailMedia = (
  media: CombinedMedia['mediaArrayWithThumbnails'][number]
): media is Queries.muxVideoFragmentFragment & {
  isThumbnail: boolean;
} => {
  return (
    (
      media as Queries.muxVideoFragmentFragment & {
        isThumbnail: boolean;
      }
    )?.isThumbnail !== undefined
  );
};
